// Full-detail error modal for a failed transaction-screen request — message,
// HTTP status, error code, and (when the backend attached it) the
// server-side traceback — with a one-click "Copy" button.
//
// This is the PACKAGE fallback (self-contained overlay; the platform's
// chrome lives in its own Modal component, so the designer injects its
// existing ErrorDetailsModal via <TxnScreen renderErrorDetails> to stay
// pixel-identical). The `ErrorReport` shape is shared by both.

import { useState } from 'react'
import { Check, Copy, AlertTriangle, X } from 'lucide-react'
import { formatErrorReport } from '../utils'
import type { ParsedApiError } from '../utils'

export interface ErrorReport {
  context: string
  parsed: ParsedApiError
}

interface Props {
  report: ErrorReport | null
  onClose: () => void
}

export default function ErrorDetailsModal({ report, onClose }: Props) {
  const [copied, setCopied] = useState(false)

  if (!report) return null

  const fullText = formatErrorReport(report.context, report.parsed)

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullText)
      } else {
        // Fallback for non-secure contexts where the async clipboard API
        // is unavailable: stage the text in a hidden textarea and use the
        // legacy execCommand copy.
        const ta = document.createElement('textarea')
        ta.value = fullText
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          width: 'min(960px, 96vw)',
          maxHeight: '88vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <strong style={{ fontSize: '0.9625rem' }}>Request error</strong>
          <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>
        <div style={{
          flex: 1, overflow: 'auto', padding: 18,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            color: '#ef4444', fontSize: '0.9125rem', fontWeight: 600,
          }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{report.context}</span>
          </div>
          <pre style={{
            margin: 0,
            maxHeight: '60vh',
            overflow: 'auto',
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            padding: 12,
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: '0.8225rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'var(--color-text)',
          }}>
            {fullText}
          </pre>
        </div>
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--color-border)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={copy}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy error'}
          </button>
        </div>
      </div>
    </div>
  )
}
